import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Loader2 } from "lucide-react";

/**
 * RenderPreviewSwatch
 * -------------------
 * Live preview of the admin's *unsaved* render config. Hits
 * `POST /api/admin/render/preview` with the current form values so the
 * pixels the admin sees are the SAME pyrender matte pipeline that
 * produces real seller renders — not a Three.js approximation. This is
 * what lets the admin verify "changing object colour actually changes
 * the colour" before saving.
 *
 * Debounced so slider drags don't spam the render worker.
 */
const SIZE = 220;

export default function RenderPreviewSwatch({ cfg, testid = "render-preview-swatch" }) {
  const [dataUrl, setDataUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const timer = useRef(null);
  // 2026-02 reference-match defaults — kept in sync with backend
  // `RenderConfig` and the seed migration that cleared stale settings.
  const objectHex = (cfg?.render_object_color_hex || "#C8CCD0").toUpperCase();
  const bgHex = (cfg?.render_bg_color_hex || "#0F0F10").toUpperCase();

  useEffect(() => {
    if (!cfg) return;
    clearTimeout(timer.current);
    // 400ms debounce is enough that a slider drag doesn't spam the render
    // worker but a colour pick / typed hex feels instantaneous.
    timer.current = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const body = {
          render_object_color_hex: cfg.render_object_color_hex,
          render_bg_color_hex: cfg.render_bg_color_hex,
          render_object_specular: cfg.render_object_specular,
          render_object_metallic: cfg.render_object_metallic,
          render_ao_strength: cfg.render_ao_strength,
          render_frame_fill: cfg.render_frame_fill,
          render_light_intensity: cfg.render_light_intensity,
        };
        const res = await api.post("/admin/render/preview", body, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        setDataUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      } catch (e) {
        setError("Preview unavailable — settings will still be saved.");
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer.current);
  }, [
    cfg?.render_object_color_hex,
    cfg?.render_bg_color_hex,
    cfg?.render_object_specular,
    cfg?.render_object_metallic,
    cfg?.render_ao_strength,
    cfg?.render_frame_fill,
    cfg?.render_light_intensity,
  ]);

  // Free the object URL when the component unmounts.
  useEffect(() => () => { if (dataUrl) URL.revokeObjectURL(dataUrl); }, []);// eslint-disable-line

  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <div
        className="rounded-md border border-border overflow-hidden relative"
        style={{ width: SIZE, height: SIZE, background: bgHex }}
      >
        {dataUrl && (
          <img
            src={dataUrl}
            alt="Render preview"
            width={SIZE}
            height={SIZE}
            className="block"
          />
        )}
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-[1px]">
            <Loader2 className="h-5 w-5 animate-spin text-slate-700" />
          </div>
        )}
      </div>
      <div className="text-xs font-mono space-y-1">
        <div>Object <b>{objectHex}</b></div>
        <div>Background <b>{bgHex}</b></div>
        {error ? (
          <div className="text-[10px] text-amber-600 uppercase tracking-widest mt-2">{error}</div>
        ) : (
          <div className="text-[10px] text-muted-foreground uppercase tracking-widest mt-2">
            Live · Same pipeline as seller renders
          </div>
        )}
      </div>
    </div>
  );
}
